import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { Alert, Button, Flex, Spin, Typography, theme } from 'antd';
import dayjs from 'dayjs';
import { ArrowLeftOutlined, FilePdfOutlined } from '@ant-design/icons';

import {
  buildDiagnosticsReportViewedProperties,
  createDiagnosticsRequestId,
} from '../DiagnosticsAnalyticsEvents';
import { useGetDiagnosticsReportQuery } from '../api/diagnosticsReportApi';
import DiagnosticsCheckRowCard, { CHECK_ROW_LABEL_BASIS } from './DiagnosticsCheckRowCard';
import DiagnosticsSummaryCard from './DiagnosticsSummaryCard';
import RerunPlanGate from '../components/RerunPlanGate';
import { REPORT_NOTE_MAX_TEXT_WIDTH } from './reportNoteTextWidth';
import {
  extractDomain,
  getBlockedProvidersFromRows,
  getCheckRows,
  getCrawlersTested,
} from '../diagnosticsReportUtils';
import useExportReport from '../hooks/useExportReport';
import type { DiagnosticsRerunLocationState } from '../DiagnosticsTypes';
import useTrackDiagnosticsEvent from '../hooks/useTrackDiagnosticsEvent';
import { useIsPrerenderUser } from '../../../../hooks/usePrerenderUser';
import { PointInTime } from '~components/PointInTime';
import { dates } from '~library/dates';

type DiagnosticsReportViewProps = {
  print?: boolean;
};

export default function DiagnosticsReportView({ print = false }: DiagnosticsReportViewProps) {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const exportReport = useExportReport();
  const trackDiagnosticsEvent = useTrackDiagnosticsEvent();
  const isPrerenderUser = useIsPrerenderUser();

  const { data: report, isLoading, isError } = useGetDiagnosticsReportQuery(reportId ?? skipToken);

  const checkRows = useMemo(() => (report ? getCheckRows(report.userAgents) : []), [report]);
  const blockedProviders = useMemo(() => getBlockedProvidersFromRows(checkRows), [checkRows]);
  const crawlersTested = useMemo(() => (report ? getCrawlersTested(report.userAgents) : []), [report]);
  const domain = report ? extractDomain(report.url) : '';

  // Only offer a rerun when there is a reason to: a blocked crawler to unblock,
  // or a check that failed to run and is worth retrying.
  const hasFailedCheck = checkRows.some((row) => row.failed);
  const canRerun = blockedProviders.length > 0 || hasFailedCheck;

  // All-clear only when no crawler is blocked AND every check actually ran. If any
  // check failed to run we can't confirm the site is fully open, so the reassuring
  // "crawlers appear once they visit" note must stay hidden (PRE-3158, PO Venessa).
  const isAllClear = blockedProviders.length === 0 && !hasFailedCheck;

  // Fire one impression per opened report. Keying the guard on reportId (not a
  // bare boolean) means an RTK Query refetch — window refocus, cache
  // invalidation — that swaps the `report` object identity does not re-fire,
  // while genuinely opening a different report still does.
  const reportViewedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!report || reportViewedRef.current === reportId) return;
    reportViewedRef.current = reportId;
    trackDiagnosticsEvent('Diagnostics: Report Viewed', buildDiagnosticsReportViewedProperties(report));
  }, [report, reportId, trackDiagnosticsEvent]);

  useEffect(() => {
    if (print && report) {
      document.title = `diagnostic-report-${extractDomain(report.url)}-${dayjs(report.createdAt).format('YYYY-MM-DD')}`;
      window.print();
    }
  }, [print, report]);

  // The probe log is an internal debugging aid — only Prerender employees
  // (prerender.io accounts) should see it in the console.
  useEffect(() => {
    if (report && isPrerenderUser) {
      // eslint-disable-next-line no-console
      console.table(report.probes ?? []);
    }
  }, [report, isPrerenderUser]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (isError || !report) {
    return (
      <Flex vertical gap={token.marginMD} align="center" justify="center" style={{ minHeight: 400 }}>
        <Typography.Text type="secondary">Report not found</Typography.Text>
        <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/ai-insights/diagnostics')}>
          Go Back to Diagnostics
        </Button>
      </Flex>
    );
  }

  return (
    <Flex vertical gap={token.marginLG} style={{ padding: token.paddingLG }}>
      {/* Header */}
      <Flex justify="space-between" align="flex-start">
        <Flex gap={token.marginXS} align="center">
          {!print && (
            <ArrowLeftOutlined style={{ fontSize: token.fontSizeHeading3, cursor: 'pointer', color: token.colorPrimary }} onClick={() => navigate('/ai-insights/diagnostics')} />
          )}
          <div>
            <Typography.Title level={2} style={{ margin: 0 }}>
              AI Access Diagnostics Report
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              Generated: {print ? dates.pointInTimeWithOffset(report.createdAt) : <PointInTime value={report.createdAt} />}
            </Typography.Text>
          </div>
        </Flex>
        {!print && (
          <Button
            icon={<FilePdfOutlined />}
            onClick={() => exportReport(report.id, 'report_page')}
          >
            Export report
          </Button>
        )}
      </Flex>

      <DiagnosticsSummaryCard
        domain={domain}
        url={report.url}
        blockedProviders={blockedProviders}
        checkRows={checkRows}
        crawlersTested={crawlersTested}
        print={print}
        note={
          isAllClear ? (
            <Alert
              type="info"
              showIcon
              style={{ fontSize: token.fontSizeSM }}
              message={
                <span style={{ display: 'block', maxWidth: REPORT_NOTE_MAX_TEXT_WIDTH }}>
                  Diagnostics confirmed your site allows access to these AI crawlers. They’re flagged as missing in your discoverability tab because they haven’t visited in the last 7 days. Once a crawler visits, the flag clears and its activity appears in your crawler requests chart.
                </span>
              }
            />
          ) : undefined
        }
      />

      {/* Access Requirements */}
      <div>
        <Typography.Title level={4} style={{ margin: 0, fontSize: token.fontSizeHeading4 }}>
          Access Requirements
        </Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          All checks must pass for AI crawlers to reach your site
        </Typography.Text>
      </div>

      {/* Check cards — a plain block container (NOT a flex box) so the browser keeps each
          card whole across a print page break. break-inside: avoid is ignored on table row
          groups AND on flex items in Safari/WebKit, so each card must sit in normal block
          flow; inter-card spacing lives on the card's marginBottom (PRE-2920). */}
      <div>
        {/* 1px transparent border matches the cards' border inset below, so the column
            labels line up with the card content. */}
        <Flex align="center" gap={token.marginXS} style={{ padding: `0 ${token.paddingMD}px`, border: '1px solid transparent', marginBottom: token.marginMD }}>
          <div style={{ flex: `0 0 ${CHECK_ROW_LABEL_BASIS}` }}>
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              Check Details
            </Typography.Text>
          </div>
          <Flex flex="1">
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              Blocked AI Crawlers
            </Typography.Text>
          </Flex>
        </Flex>

        {checkRows.map((row) => (
          <DiagnosticsCheckRowCard key={row.checkKey} row={row} report={report} trackEvent={trackDiagnosticsEvent} print={print} />
        ))}
      </div>

      {/* Footer */}
      {!print && (
        <Flex justify="space-between" align="center">
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/ai-insights/diagnostics')}>
            Go Back to Diagnostics
          </Button>

          {canRerun && (
            <RerunPlanGate
              render={(rerunDisabled) => (
                <Button
                  disabled={rerunDisabled}
                  onClick={() => {
                    const requestId = createDiagnosticsRequestId();
                    trackDiagnosticsEvent('Diagnostics: Run Clicked', {
                      domain,
                      bots_to_test: '',
                      run_trigger: 'report_page_rerun_diagnostics',
                      request_id: requestId,
                    });
                    const state: DiagnosticsRerunLocationState = {
                      rerunDiagnostics: { domain, requestId },
                    };
                    navigate('/ai-insights/diagnostics', { state });
                  }}
                >
                  Rerun diagnostics
                </Button>
              )}
            />
          )}
        </Flex>
      )}
    </Flex>
  );
}
